import { TransactionType } from '@converge/graphql';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ModalStep } from '../insured-policies.detail';
import { ModalConfigType } from './midterm-transaction-modal';

export interface MidtermTransactionModalProps {
  onClose: () => void;
  policyUuid: string;
  effectiveDate?: string;
  maxDate?: Date;
  transactionType: TransactionType;
  setOpenNextPage?: Dispatch<SetStateAction<boolean>>;
}

type UseModalStepperProps = {
  modalConfig: ModalConfigType;
  firstStep: ModalStep;
  onClose: () => void;
  setIsDisabled: (value: boolean) => void;
  onFinalStepSubmit: () => void;
  hasOpenClaims?: boolean;
};

export const useModalStepper = ({
  modalConfig,
  firstStep,
  onClose,
  setIsDisabled,
  onFinalStepSubmit,
  hasOpenClaims = false,
}: UseModalStepperProps) => {
  const [currentStep, setCurrentStep] = useState<ModalStep>(firstStep);

  useEffect(() => {
    setCurrentStep(firstStep);
  }, [firstStep]);

  useEffect(() => {
    if (currentStep === ModalStep.REINSTATEMENT_DETAILS) {
      setIsDisabled(false);
    }
  }, [currentStep, setIsDisabled]);

  const handleNextPage = () => {
    if (
      currentStep === ModalStep.TRANSACTION_NOTE ||
      currentStep === ModalStep.REINSTATEMENT_DETAILS
    ) {
      onFinalStepSubmit();
    } else {
      changeNextPage();
    }
  };

  const handleOnClose = useCallback(() => {
    setIsDisabled(true);
    setCurrentStep(firstStep);
    onClose();
  }, [onClose, setIsDisabled, firstStep]);

  const changeNextPage = useCallback(() => {
    const nextStep = modalConfig[currentStep].nextStep;
    if (nextStep) {
      setCurrentStep(nextStep);
      if (nextStep !== ModalStep.REINSTATEMENT_DETAILS) {
        setIsDisabled(true);
      }
    } else {
      handleOnClose();
    }
  }, [currentStep, setIsDisabled, modalConfig, handleOnClose]);

  const handleBackPage = useCallback(() => {
    const reverseStepTransitions: Partial<Record<ModalStep, ModalStep>> = {
      [ModalStep.CANCELLATION_DETAILS]: hasOpenClaims
        ? ModalStep.ACTIVE_CLAIM_INFO
        : undefined,
      [ModalStep.TRANSACTION_NOTE]: ModalStep.CANCELLATION_DETAILS,
    };

    if (
      currentStep === ModalStep.INACTIVE_INFO ||
      currentStep === ModalStep.ACTIVE_CLAIM_INFO ||
      currentStep === ModalStep.REINSTATEMENT_DETAILS
    ) {
      handleOnClose();
    } else {
      const prev = reverseStepTransitions[currentStep];
      if (prev) {
        setCurrentStep(prev);
        setIsDisabled(false);
      } else {
        // If no previous step, close the modal
        handleOnClose();
      }
    }
  }, [currentStep, setIsDisabled, handleOnClose, hasOpenClaims]);

  return {
    currentStep,
    setIsDisabled,
    handleNextPage,
    handleBackPage,
  };
};
